import type { ActionType, ResourceKind } from "@cc/protocol";
import type { MatchHighlight, TimelineEntry } from "./match-client";

/**
 * Localisation.
 *
 * Two rules shape this file:
 *
 *  1. **The engine stays language-agnostic.** It emits English *identifiers*
 *     (`"moving to resource"`, `"THE COLLECTOR"`), which are stable keys. All
 *     translation happens here, at the edge. Putting display strings into the
 *     simulation would leak presentation into the replay/action-log format.
 *
 *  2. **No rendered text is stored as state.** The timeline keeps structured
 *     data and is turned into prose at render time. An earlier version baked
 *     English sentences into timeline entries, which meant switching language
 *     would either leave old rows in English or force a full match restart.
 *
 * The agent `reason` strings are a closed set, so they are translated by lookup.
 * A real LLM's own reason text is free-form and passes through untranslated —
 * which is correct, it is that model's voice and we should not put words in its
 * mouth. `test/i18n.test.ts` derives the closed set from the agent sources
 * rather than a hand-copied list, so adding a new bot reason without translating
 * it fails the build.
 */
export type Locale = "en" | "zh";

export const LOCALES: readonly Locale[] = ["en", "zh"];

const STORAGE_KEY = "cyber-cricket-locale";

/* ------------------------------------------------------------------ */
/* Locale detection and persistence                                    */
/* ------------------------------------------------------------------ */

function normalise(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v.startsWith("zh") || v === "cn" || v === "chinese") return "zh";
  if (v.startsWith("en")) return "en";
  return null;
}

/**
 * Picks the starting language.
 *
 * Explicit intents win in order: a `?lang=` parameter (so a shared link opens in
 * the language it was shared in), then a previously stored choice, then the
 * browser's own preference. The browser fallback is what lets a Chinese visitor
 * see Chinese immediately without touching anything.
 *
 * `index.html` runs the same three checks inline before the bundle loads, so the
 * first paint is already correct rather than flashing English.
 */
export function detectInitialLocale(): { locale: Locale; explicit: boolean } {
  if (typeof window === "undefined") return { locale: "en", explicit: false };

  const fromUrl = normalise(new URLSearchParams(window.location.search).get("lang"));
  if (fromUrl) return { locale: fromUrl, explicit: true };

  try {
    const stored = normalise(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return { locale: stored, explicit: true };
  } catch {
    // Private mode / storage disabled: fall through to browser detection.
  }

  const candidates = [...(navigator.languages ?? []), navigator.language].filter(
    Boolean,
  ) as string[];

  for (const candidate of candidates) {
    const match = normalise(candidate);
    if (match) return { locale: match, explicit: false };
  }
  return { locale: "en", explicit: false };
}

const TITLES: Record<Locale, string> = {
  en: "CYBER CRICKET — where AI learns to fight",
  zh: "赛博斗蛐蛐 — 让 AI 自己打架",
};

const DESCRIPTIONS: Record<Locale, string> = {
  en: "Two AI models each control a cyber cricket in a live arena. Watch what they do, not what they say.",
  zh: "两个 AI 各控制一只赛博蟋蟀，在同一张地图上实时对战。看它们做了什么，而不是说了什么。",
};

/**
 * Applies everything that lives on the document rather than in the React tree.
 *
 * `lang` is not cosmetic: browsers use it for font selection, line-breaking and
 * hyphenation. The title and description are what a shared link unfurls as, so
 * they follow the page language too.
 */
export function applyDocumentLocale(locale: Locale): void {
  document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  document.title = TITLES[locale];
  const meta = document.querySelector('meta[name="description"]');
  if (meta) meta.setAttribute("content", DESCRIPTIONS[locale]);
}

export function persistLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not being able to remember the choice is not worth breaking the page over.
  }
  applyDocumentLocale(locale);
}

/* ------------------------------------------------------------------ */
/* Dictionary                                                          */
/* ------------------------------------------------------------------ */

/**
 * Strings are functions where they take values, rather than templates with
 * placeholder tokens. It is more code, but the compiler then checks that every
 * locale supplies every form, and no locale can silently forget an argument.
 */
export interface Dict {
  /* Chrome */
  readonly tagline: string;
  readonly pause: string;
  readonly resume: string;
  readonly newMatch: string;
  readonly languageName: string;

  /* HUD */
  readonly live: string;
  readonly ended: string;
  readonly hp: string;
  readonly collecting: string;
  readonly defending: string;
  /** Short status badge, e.g. the "ELIMINATED" chip in the HUD. */
  readonly eliminatedBadge: string;
  readonly phaseEarly: string;
  readonly phaseMid: string;
  readonly phaseLate: string;
  readonly phaseFinal: string;
  readonly resShort: string;

  /* Timeline */
  readonly decisionTimeline: string;
  readonly movesCount: (n: number) => string;
  readonly keyCount: (n: number) => string;
  readonly waitingFirstDecision: string;
  readonly whyTitle: string;
  readonly close: string;
  readonly position: string;
  readonly hpScore: string;
  readonly enemy: string;
  readonly enemyVisible: (x: number, y: number, d: string) => string;
  readonly enemyStale: string;
  readonly enemyNeverSeen: string;
  readonly resourcesInSight: string;
  readonly distanceTag: (d: string) => string;
  readonly inRange: string;
  readonly resourceAsTrap: (id: string) => string;
  readonly resourcePlain: (kind: string, id: string) => string;
  readonly observationUnavailable: string;
  readonly visionNote: string;

  /* Timeline event prose */
  readonly collected: (who: string, kind: string, value: number) => string;
  readonly steppedOnTrap: (who: string) => string;
  readonly detectedTrap: (who: string) => string;
  readonly interrupted: (who: string) => string;
  readonly collectMissed: (who: string) => string;
  readonly killedByTrap: (who: string) => string;
  readonly eliminated: (who: string) => string;
  readonly channelLost: string;
  readonly collectMissedFloat: string;

  /* Result */
  readonly draw: string;
  readonly wins: (who: string) => string;
  readonly reasonScore: string;
  readonly reasonElimination: string;
  readonly reasonTimeout: string;
  readonly reasonDraw: string;
  readonly keyMoments: string;
  readonly finishedWithHp: (hp: number) => string;
  readonly secondsShort: (s: number) => string;
  /** Restart with the same fighters / AI config. */
  readonly rematch: string;
  /** Open the pick-both-sides duel modal. */
  readonly newDuel: string;
  /** Dismiss the settlement modal and show the battle timeline. */
  readonly viewDetails: string;

  /* Highlights */
  readonly momentEnergy: (who: string) => string;
  readonly momentTrap: (who: string) => string;
  readonly momentEliminated: (who: string) => string;
  readonly momentInterrupt: (who: string) => string;

  /* Stats */
  readonly statCollected: string;
  readonly statAttacks: string;
  readonly statDamage: string;
  readonly statTraps: string;

  /* Roster */
  readonly crickets: string;
  readonly rosterStep1: string;
  readonly rosterStep2: string;
  readonly rosterBotsNote: string;
  readonly rosterPhase3: string;
  readonly noApiKey: string;
  readonly blurbCollector: string;
  readonly blurbScout: string;
  readonly blurbHunter: string;

  /* Traits */
  readonly traitAggression: string;
  readonly traitExploration: string;
  readonly traitRisk: string;
  readonly traitResourceFocus: string;
  readonly traitIntel: string;
  readonly traitPatience: string;
  readonly traitSurvival: string;
  readonly traitPrecision: string;
  /* Navigation */
  readonly navLive: string;
  readonly navRules: string;
  /* Live AI panel (session only, never persisted) */
  readonly aiPanelTitle: string;
  readonly aiPanelIntro: string;
  readonly aiProvider: string;
  readonly aiModel: string;
  readonly aiApiKey: string;
  readonly aiBaseUrl: string;
  readonly aiCustom: string;
  readonly aiName: string;
  readonly aiNamePlaceholder: string;
  readonly aiIcon: string;
  readonly aiIconDefault: string;
  readonly aiIconHint: string;
  readonly aiIconPreview: string;
  readonly aiThinking: string;
  readonly aiEnable: string;
  readonly aiDisable: string;
  readonly aiActive: string;
  readonly aiTest: string;
  readonly aiTesting: string;
  readonly aiConnOk: (ms: number, reply: string) => string;
  readonly aiConnAuth: string;
  readonly aiConnModel: string;
  readonly aiConnNetwork: string;
  readonly aiSecurity: string;
  readonly aiBlurb: string;
  readonly observedStyle: string;
  readonly aiDegraded: (name: string) => string;
  readonly aiDegradedHint: string;
  readonly aiFallbacks: (n: number) => string;
  /* Replay */
  readonly replayTitle: string;
  readonly replayInvalid: string;
  readonly replayNote: string;
  readonly replayAiNote: string;
  readonly liveTitle: string;
  readonly visionPanel: string;
  readonly visionPanelEmpty: string;
  readonly visionPanelHint: string;
  readonly rulesStripHint: string;
  readonly stripNothing: string;
  readonly stripLandmine: (dmg: number) => string;
  readonly stripHeals: (heal: number) => string;
  readonly stripObjective: string;
  readonly stripVision: string;
  readonly stripScoring: string;
  readonly stripTipsTitle: string;
  readonly stripTip1: string;
  readonly stripTip2: string;
  readonly stripTip3: string;
  readonly duelModalTitle: string;
  readonly duelModalIntro: string;
  readonly duelSideLeft: string;
  readonly duelSideRight: string;
  readonly duelKindRules: string;
  readonly duelKindAi: string;
  readonly duelAiConfigTitle: string;
  readonly duelSameFighter: string;
  readonly duelStart: string;
  readonly back: string;

  /* Rules */
  readonly rulesTitle: string;
  readonly rulesSubtitle: string;
  readonly rulesObjective: string;
  readonly rulesObjectiveBody: string;
  readonly rulesMap: string;
  readonly rulesMapHint: string;
  readonly rulesItems: string;
  readonly rulesItemHint: string;
  readonly rulesActions: string;
  readonly rulesVision: string;
  readonly rulesVisionBody: string;
  readonly rulesArena: string;
  readonly rulesArenaBody: string;
  readonly rulesScoring: string;
  readonly rulesScoringBody: string;
  readonly rulesNumbersNote: string;
  readonly rulesPhaseNote: string;
  readonly itemScore: (n: number) => string;
  readonly itemHeals: (n: number) => string;
  readonly itemDisguised: string;
  readonly itemLandmine: string;
  readonly itemTrapPenalty: (hp: number, score: number) => string;
  readonly itemBombBrief: (dmg: number, radius: number) => string;
  readonly itemBladeBrief: (dmg: number, range: number) => string;
  readonly itemInventory: string;
  readonly itemExchangeCost: (cost: number) => string;
  readonly fled: (who: string) => string;
  readonly exchanged: (who: string, item: string, cost: number) => string;
  readonly usedItem: (who: string, item: string) => string;
  readonly detonatedBomb: (who: string, dmg: number) => string;
  readonly spunBlade: (who: string) => string;
  readonly inventoryTag: (bombs: number, blades: number) => string;
  readonly totalScoreLabel: string;

  /* Vision */
  readonly visionSpectator: string;
  readonly visionOf: (who: string) => string;
  readonly visionWhatTheySee: string;
  readonly visionSpectatorHint: string;
  readonly visionBlindTo: string;

  /* Split timeline / battle timeline */
  readonly decisionHeader: string;
  readonly battleTimeline: string;
  readonly battleTimelineHint: string;
  readonly notVisible: string;
  readonly noDecisions: string;


}

const EN: Dict = {
  tagline: "where AI learns to fight",
  pause: "⏸ PAUSE",
  resume: "▶ RESUME",
  newMatch: "▸ START DUEL",
  languageName: "中文",

  live: "LIVE",
  ended: "ENDED",
  hp: "HP",
  collecting: "COLLECTING",
  defending: "DEFENDING",
  eliminatedBadge: "ELIMINATED",
  phaseEarly: "EARLY",
  phaseMid: "MID",
  phaseLate: "LATE",
  phaseFinal: "FINAL STEPS",
  resShort: "res",

  decisionTimeline: "Decision Timeline",
  movesCount: (n) => `${n} moves`,
  keyCount: (n) => `${n} key`,
  waitingFirstDecision: "waiting for the first decision…",
  whyTitle: "🧠 Why?",
  close: "close",
  position: "position",
  hpScore: "hp / score",
  enemy: "enemy",
  enemyVisible: (x, y, d) => `visible at [${x}, ${y}] (${d})`,
  enemyStale: "not visible · stale intel only",
  enemyNeverSeen: "never seen",
  resourcesInSight: "resources in sight",
  distanceTag: (d) => `d=${d}`,
  inRange: "IN RANGE",
  resourceAsTrap: (id) => `💀 trap · ${id}`,
  resourcePlain: (kind, id) => `${kind} · ${id}`,
  observationUnavailable: "observation unavailable for this tick",
  visionNote:
    "A cricket only ever sees what falls inside its vision radius. Nothing beyond this list was available to it.",

  collected: (who, kind, value) => `${who} banked ${kind} +${value}`,
  steppedOnTrap: (who) => `💀 ${who} stepped straight into a trap`,
  detectedTrap: (who) => `${who} scouted out a hidden trap`,
  interrupted: (who) => `${who} was knocked off a resource`,
  collectMissed: (who) => `${who} grabbed at empty air`,
  killedByTrap: (who) => `${who} was killed by a trap`,
  eliminated: (who) => `${who} was eliminated`,
  channelLost: "CHANNEL LOST",
  collectMissedFloat: "MISS",

  draw: "DRAW",
  wins: (who) => `${who} WINS`,
  reasonScore: "highest crystal+HP total at the final whistle",
  reasonElimination: "elimination",
  reasonTimeout: "final whistle",
  reasonDraw: "dead heat",
  keyMoments: "Key Moments",
  finishedWithHp: (hp) => `finished with ${hp} HP`,
  secondsShort: (s) => `${s}s`,
  rematch: "▸ REMATCH",
  newDuel: "▸ NEW DUEL",
  viewDetails: "VIEW DETAILS",

  momentEnergy: (who) => `${who} secured a 25-point energy`,
  momentTrap: (who) => `${who} stepped straight into a trap`,
  momentEliminated: (who) => `${who} was eliminated`,
  momentInterrupt: (who) => `${who} lost a resource channel`,

  statCollected: "collected",
  statAttacks: "attacks",
  statDamage: "damage",
  statTraps: "traps",

  crickets: "Crickets",
  rosterStep1: "Pick a cricket to test it against the current opponent.",
  rosterStep2: "Hit",
  rosterBotsNote: "These three are deterministic bots, so the site runs with",
  rosterPhase3: "Real LLM crickets use the same engine — see Phase 3.",
  noApiKey: "no API key and no cost",
  blurbCollector: "Sees only the crystals. Maximum greed, minimum caution.",
  blurbScout: "Scans before committing. Slower, but rarely blown up.",
  blurbHunter: "Ignores the economy and hunts the other cricket.",

  traitAggression: "Aggression",
  traitExploration: "Exploration",
  traitRisk: "Risk taking",
  traitResourceFocus: "Resource focus",
  traitIntel: "Scouting",
  traitPatience: "Patience",
  traitSurvival: "Survival",
  traitPrecision: "Precision",
  navLive: "LIVE",
  navRules: "RULES",
  aiPanelTitle: "AI CRICKET (OPTIONAL)",
  aiPanelIntro:
    "Paste a key and a real model plays the next match. No account, no login, nothing saved.",
  aiProvider: "Provider",
  aiModel: "Model",
  aiApiKey: "API Key",
  aiBaseUrl: "Base URL",
  aiCustom: "Custom",
  aiName: "Name",
  aiNamePlaceholder: "RED NOVA",
  aiIcon: "Icon",
  aiIconDefault: "Provider logo",
  aiIconHint: "Provider logo is used by default; pick an emoji to override",
  aiIconPreview: "Ball preview",
  aiThinking: "thinking…",
  aiEnable: "PUT AI IN ARENA",
  aiDisable: "REMOVE AI",
  aiActive: "AN AI IS IN THIS MATCH",
  aiTest: "TEST CONNECTION",
  aiTesting: "Testing\u2026",
  aiConnOk: (ms, reply) => `Connected in ${ms}ms \u00b7 replied "${reply}"`,
  aiConnAuth: "Authentication failed. Check the API key.",
  aiConnModel: "The provider rejected the model name.",
  aiConnNetwork: "Could not reach the provider. Check the Base URL.",
  aiSecurity:
    "Your key stays in this browser tab's memory. It is never saved, and it is sent only to the provider you chose.",
  aiBlurb: "a live model, running in your browser",
  observedStyle: "OBSERVED STYLE",
  aiDegraded: (name) => `${name} IS NOT RUNNING: the model stopped answering`,
  aiDegradedHint:
    "Its provider failed repeatedly, so a built-in bot has been playing in its place. The rest of the match is a bot match.",
  aiFallbacks: (n) => `${n} calls fell back to the built-in bot`,
  replayTitle: "REPLAY",
  replayInvalid: "That replay link is not readable. A link looks like /replay/collector~hunter~421337.",
  replayNote:
    "Nothing was saved. This match is being rebuilt from the link alone, by re-running the same deterministic engine.",
  liveTitle: "LIVE",
  visionPanel: "WHAT IT SEES",
  visionPanelEmpty: "Put an AI cricket in the arena to see through its eyes.",
  visionPanelHint:
    "The same arena, drawn with only what this cricket can currently see. Items it has not found stay hidden, and it cannot see its opponent outside the ring. If it walks past something valuable, that is why.",
  rulesStripHint: "Full rulebook",
  stripNothing: "hold",
  stripLandmine: (dmg) => `−${dmg} HP`,
  stripHeals: (heal) => `heals ${heal}`,
  stripObjective:
    "Highest (crystal score + remaining HP) after 90 decision steps wins. One step = one abstract second. Model latency does not eat arena time. You see everything; each cricket only sees its vision circle.",
  stripVision:
    "Vision is larger than before, but still far from the whole map. Walking past loot is fair — it was never in range.",
  stripScoring:
    "Ranking total = crystal score + remaining HP. Chip the enemy to climb the board; elimination does not end the match.",
  stripTipsTitle: "QUICK RULES",
  stripTip1: "Traps look like crystals until scouted or stepped on.",
  stripTip2: "Bombs and blades cut HP fast — pick them up or EXCHANGE crystals for them.",
  stripTip3: "ATTACK knocks the enemy away; FLEE sprints out of a clump.",
  duelModalTitle: "START DUEL",
  duelModalIntro:
    "Pick both fighters. Each side can be a rule bot or a live AI cricket — including AI vs AI.",
  duelSideLeft: "SIDE A",
  duelSideRight: "SIDE B",
  duelKindRules: "Rule cricket",
  duelKindAi: "AI cricket",
  duelAiConfigTitle: "AI CONFIG",
  duelSameFighter: "Pick two different rule crickets, or put an AI on at least one side.",
  duelStart: "▸ START DUEL",
  replayAiNote:
    "This match involved a live AI cricket. Its decisions came from a model over the network, so this is a fresh play-out rather than the original game.",
  back: "BACK",

  rulesTitle: "HOW TO PLAY",
  rulesSubtitle:
    "The complete Cyber Cricket rulebook. Nothing here is hidden from you — the crickets just don't get to read it.",
  rulesObjective: "OBJECTIVE",
  rulesObjectiveBody:
    "Bank crystal score and keep your HP. When the whistle blows, the ranking total is crystal score + remaining HP — so farmers and attackers both have a path to win. A match is 90 decision steps (90 abstract seconds): each cricket chooses once per step, then the arena advances. Wall-clock thinking time is held out of the match clock, so a slow model is not punished for latency. You can see everything; each cricket sees only what is inside its own vision radius.",
  rulesMap: "MAP",
  rulesMapHint: "Both spawn in opposite corners, with a glowing vision radius around each one.",
  rulesItems: "ITEMS",
  rulesItemHint: "Click any item to expand it. Traps are disguised as crystals — that is the whole drama.",
  rulesActions: "ACTIONS",
  rulesVision: "VISION",
  rulesVisionBody:
    "Each cricket sees only what falls inside its own vision radius. The spectator sees the whole arena, which is why an AI can look stupid in a way that is actually completely fair: it genuinely could not see the trap.",
  rulesArena: "THE ARENA",
  rulesArenaBody:
    "A square arena. Both crickets start in opposite corners and there is exactly one opponent — no teams, no diplomacy, no talking.",
  rulesScoring: "SCORING",
  rulesScoringBody:
    "Ranking total = crystal score + remaining HP. Highest total at the final whistle wins. Being eliminated does not end the match; a dead cricket simply stops earning and contributes 0 HP, so survival and damage both matter.",
  rulesNumbersNote:
    "Every number on this page is read from the same constants the simulation uses, so the rules cannot drift from the game.",
  rulesPhaseNote:
    "The 90-step match plays out in EARLY, MID, LATE and FINAL STEPS phases. The HUD clock counts abstract seconds — one per decision — not wall-clock time.",
  itemScore: (n) => `+${n} score`,
  itemHeals: (n) => `heals ${n} HP`,
  itemDisguised: "disguised as a crystal",
  itemLandmine: "explodes on contact",
  itemTrapPenalty: (hp, score) => `−${hp} HP, −${score} score`,
  itemBombBrief: (dmg, radius) => `inventory · USE deals ${dmg} AoE (r${radius})`,
  itemBladeBrief: (dmg, range) => `inventory · USE spins for ${dmg}/tick (r${range})`,
  itemInventory: "goes into inventory — USE to activate",
  itemExchangeCost: (cost) => `or EXCHANGE for ${cost} crystal score`,
  fled: (who) => `${who} fled the fight`,
  exchanged: (who, item, cost) => `${who} exchanged −${cost} for ${item}`,
  usedItem: (who, item) => `${who} used ${item}`,
  detonatedBomb: (who, dmg) => `💥 ${who} detonated a bomb (−${dmg})`,
  spunBlade: (who) => `🌀 ${who} spun up a blade`,
  inventoryTag: (bombs, blades) => `💣${bombs} · ✸${blades}`,
  totalScoreLabel: "total",

  visionSpectator: "SPECTATOR",
  visionOf: (who) => `${who}'s VISION`,
  visionWhatTheySee: "What it can see",
  visionSpectatorHint: "You are seeing everything. Press ⇧V to jump into a cricket's eyes.",
  visionBlindTo: "blind to the rest",

  decisionHeader: "DECISION LOG",
  battleTimeline: "BATTLE TIMELINE",
  battleTimelineHint: "Both crickets, merged onto one clock. This is the story of the match.",
  notVisible: "not visible",
  noDecisions: "no decisions yet",


};

const ZH: Dict = {
  tagline: "让 AI 自己打架，人类负责围观",
  pause: "⏸ 暂停",
  resume: "▶ 继续",
  newMatch: "▸ 开始对决",
  languageName: "EN",

  live: "直播中",
  ended: "已结束",
  hp: "血量",
  collecting: "采集中",
  defending: "防御中",
  eliminatedBadge: "已淘汰",
  phaseEarly: "前期",
  phaseMid: "中期",
  phaseLate: "后期",
  phaseFinal: "最后时刻",
  resShort: "个资源",

  decisionTimeline: "决策时间线",
  movesCount: (n) => `${n} 步`,
  keyCount: (n) => `${n} 个关键`,
  waitingFirstDecision: "等待第一次决策…",
  whyTitle: "🧠 为什么？",
  close: "关闭",
  position: "位置",
  hpScore: "血量 / 积分",
  enemy: "对手",
  enemyVisible: (x, y, d) => `可见，位于 [${x}, ${y}]（距离 ${d}）`,
  enemyStale: "不可见 · 只有旧情报",
  enemyNeverSeen: "从未发现",
  resourcesInSight: "视野内资源",
  distanceTag: (d) => `距离 ${d}`,
  inRange: "可交互",
  resourceAsTrap: (id) => `💀 陷阱 · ${id}`,
  resourcePlain: (kind, id) => `${kind} · ${id}`,
  observationUnavailable: "该时刻的观测数据不可用",
  visionNote: "蟋蟀只能看到视野半径内的东西。这份列表之外的信息，它一概不知道。",

  collected: (who, kind, value) => `${who} 入账${kind} +${value}`,
  steppedOnTrap: (who) => `💀 ${who} 一脚踩进了陷阱`,
  detectedTrap: (who) => `${who} 提前排查出一个陷阱`,
  interrupted: (who) => `${who} 的采集被击退打断`,
  collectMissed: (who) => `${who} 抓空了（脚下没有晶体）`,
  killedByTrap: (who) => `${who} 被陷阱炸死`,
  eliminated: (who) => `${who} 被淘汰`,
  channelLost: "采集被打断",
  collectMissedFloat: "抓空",

  draw: "平局",
  wins: (who) => `${who} 获胜`,
  reasonScore: "终场哨响时晶体分+血量总和领先",
  reasonElimination: "击倒对手",
  reasonTimeout: "终场哨响",
  reasonDraw: "不分胜负",
  keyMoments: "关键镜头",
  finishedWithHp: (hp) => `结束时剩余 ${hp} 点血量`,
  secondsShort: (s) => `${s} 秒`,
  rematch: "▸ 重新对决",
  newDuel: "▸ 新的对决",
  viewDetails: "查看详情",

  momentEnergy: (who) => `${who} 抢下 25 分能量`,
  momentTrap: (who) => `${who} 一脚踩进了陷阱`,
  momentEliminated: (who) => `${who} 被淘汰`,
  momentInterrupt: (who) => `${who} 的采集节奏被打断`,

  statCollected: "收集",
  statAttacks: "出手",
  statDamage: "伤害",
  statTraps: "踩雷",

  crickets: "选手",
  rosterStep1: "点一个选手，试试它打不打得过当前对手。",
  rosterStep2: "然后按",
  rosterBotsNote: "这三个是确定性机器人，所以整站",
  rosterPhase3: "真实大模型用的是同一套引擎 —— 见 Phase 3。",
  noApiKey: "不需要 API key，也不花钱",
  blurbCollector: "眼里只有晶体。极度贪婪，几乎不设防。",
  blurbScout: "动手之前先扫一遍。慢一点，但很少被炸。",
  blurbHunter: "不管经济，专门去猎杀另一只蟋蟀。",

  traitAggression: "侵略性",
  traitExploration: "活动范围",
  traitRisk: "冒险倾向",
  traitResourceFocus: "资源专注",
  traitIntel: "侦察",
  traitPatience: "耐心",
  traitSurvival: "生存",
  traitPrecision: "命中率",
  navLive: "直播",
  navRules: "规则",
  aiPanelTitle: "AI 蛐蛐（可选）",
  aiPanelIntro: "填入 Key，让真实模型打下一场。不用注册、不用登录，什么都不保存。",
  aiProvider: "服务商",
  aiModel: "模型",
  aiApiKey: "API Key",
  aiBaseUrl: "Base URL",
  aiCustom: "自定义",
  aiName: "名字",
  aiNamePlaceholder: "红色新星",
  aiIcon: "图标",
  aiIconDefault: "服务商 Logo",
  aiIconHint: "默认使用服务商 Logo；也可选 emoji 覆盖",
  aiIconPreview: "小球预览",
  aiThinking: "思考中…",
  aiEnable: "让 AI 上场",
  aiDisable: "移除 AI",
  aiActive: "本场有 AI 参战",
  aiTest: "测试连接",
  aiTesting: "测试中…",
  aiConnOk: (ms, reply) => `连接成功，耗时 ${ms}ms · 回复「${reply}」`,
  aiConnAuth: "认证失败，请检查 API Key。",
  aiConnModel: "服务商不认识这个模型名。",
  aiConnNetwork: "连不上服务商，检查 Base URL。",
  aiSecurity: "你的 Key 只保存在当前标签页的内存里，不会写进磁盘，也只会发送给你选择的服务商。",
  aiBlurb: "真实模型，跑在你的浏览器里",
  observedStyle: "实测风格",
  aiDegraded: (name) => `${name} 已经掉线：模型不再应答`,
  aiDegradedHint: "它的服务商连续失败，现在是一只内置机器人替它在打。剩下的比赛实际上是机器人在打。",
  aiFallbacks: (n) => `有 ${n} 次调用退回了内置机器人`,
  replayTitle: "回放",
  replayInvalid: "这个回放链接读不出来。链接长这样：/replay/collector~hunter~421337。",
  replayNote: "没有保存任何东西。这场比赛是只凭这个链接、重跑同一套确定性引擎还原出来的。",
  liveTitle: "直播",
  visionPanel: "它看到的世界",
  visionPanelEmpty: "让一只 AI 蛐蛐上场，就能看到它眼里的世界。",
  visionPanelHint:
    "同一张地图，但只画出这只蛐蛐此刻能看到的东西。它没发现的道具会隐藏，圈外也看不到对手。如果它路过宝物却没捡，原因就在这里。",
  rulesStripHint: "完整规则",
  stripNothing: "待机",
  stripLandmine: (dmg) => `−${dmg} 血`,
  stripHeals: (heal) => `回血 ${heal}`,
  stripObjective:
    "90 次决策内，晶体分 + 剩余血量更高者获胜。一步 = 一抽象秒；模型响应快慢不吃局内时间。你能看到全图；蛐蛐只能看到自己的视野圈。",
  stripVision: "视野比以前更大，但仍远不到整张图。路过宝物却没捡，往往是因为它当时根本不在视野里。",
  stripScoring: "综合分 = 晶体分 + 剩余血量。削血也能抬分；被淘汰不会立刻结束比赛。",
  stripTipsTitle: "速览",
  stripTip1: "陷阱伪装成晶体，侦察或踩上才会暴露。",
  stripTip2: "爆炸物与旋转刀具可快速削血——地图捡取或用晶体兑换。",
  stripTip3: "攻击会击退对手；逃跑可冲刺脱离纠缠。",
  duelModalTitle: "开始对决",
  duelModalIntro: "指定对战双方。每边可以是规则蛐蛐或 AI 蛐蛐，也可以 AI 对 AI。",
  duelSideLeft: "红方 A",
  duelSideRight: "紫方 B",
  duelKindRules: "规则蛐蛐",
  duelKindAi: "AI 蛐蛐",
  duelAiConfigTitle: "AI 配置",
  duelSameFighter: "请选择两只不同的规则蛐蛐，或至少一边换成 AI。",
  duelStart: "▸ 开始对决",
  replayAiNote:
    "这场有真实 AI 蛐蛐参战。它的决策来自网络上的模型，所以这里是重新跑一遍，不是当初那一盘。",
  back: "返回",

  rulesTitle: "怎么玩",
  rulesSubtitle: "完整的赛博斗蛐蛐规则书。这里没有对你隐藏任何东西——只是蛐蛐本身看不到。",
  rulesObjective: "目标",
  rulesObjectiveBody:
    "囤晶体分，也要保住血量。终场哨响时，综合分 = 晶体分 + 剩余血量——农夫和打手都有赢面。一局 90 次决策（90 抽象秒）：每步各自选一次动作，再推进世界。模型思考的墙钟时间不计入局内时钟，所以慢模型不会因为延迟吃亏。你能看到全部信息；而每只蛐蛐只能看到自己视野半径内的东西。",
  rulesMap: "地图",
  rulesMapHint: "双方从对角出生，各自带着一圈发光的视野。",
  rulesItems: "散落物",
  rulesItemHint: "点任意一项展开。陷阱伪装成晶体——这就是全部戏剧性所在。",
  rulesActions: "动作",
  rulesVision: "视野",
  rulesVisionBody:
    "每只蛐蛐只能看到自己视野半径内的东西。观众能看到整张地图，所以一个 AI 有时看起来「很蠢」，其实完全公平：它是真的看不到那个陷阱。",
  rulesArena: "竞技场",
  rulesArenaBody:
    "一张方形地图。双方从对角出生，只有唯一一个对手——没有队友，没有外交，不能对话。",
  rulesScoring: "计分",
  rulesScoringBody:
    "综合分 = 晶体分 + 剩余血量。终场总和最高者获胜。被淘汰并不会结束比赛，死掉的蛐蛐再也赚不到分且血量按 0 计入，所以生存与输出都重要。",
  rulesNumbersNote: "本页所有数字都直接来自模拟器使用的同一份常量，规则不会和游戏脱节。",
  rulesPhaseNote:
    "90 步一局会经历前期、中期、后期、最后时刻四个阶段。HUD 时钟数的是抽象秒（一步一秒），不是墙上的真实时间。",
  itemScore: (n) => `+${n} 分`,
  itemHeals: (n) => `回复 ${n} 点血量`,
  itemDisguised: "伪装成晶体",
  itemLandmine: "接触即引爆",
  itemTrapPenalty: (hp, score) => `−${hp} 血量，−${score} 分`,
  itemBombBrief: (dmg, radius) => `进背包 · 使用造成 ${dmg} 范围伤（半径 ${radius}）`,
  itemBladeBrief: (dmg, range) => `进背包 · 使用后每 tick ${dmg} 伤（半径 ${range}）`,
  itemInventory: "进入背包，需 USE 才能发动",
  itemExchangeCost: (cost) => `也可用 ${cost} 晶体分兑换`,
  fled: (who) => `${who} 选择逃跑`,
  exchanged: (who, item, cost) => `${who} 花费 −${cost} 兑换了 ${item}`,
  usedItem: (who, item) => `${who} 使用了 ${item}`,
  detonatedBomb: (who, dmg) => `💥 ${who} 引爆了爆炸物（−${dmg}）`,
  spunBlade: (who) => `🌀 ${who} 启动了旋转刀具`,
  inventoryTag: (bombs, blades) => `💣${bombs} · ✸${blades}`,
  totalScoreLabel: "综合",

  visionSpectator: "观众视角",
  visionOf: (who) => `${who} 的视野`,
  visionWhatTheySee: "它能看到",
  visionSpectatorHint: "你现在看到的是全部信息。按 ⇧V 可以钻进某只蛐蛐的眼睛里。",
  visionBlindTo: "其余一概看不到",

  decisionHeader: "决策记录",
  battleTimeline: "对局时间轴",
  battleTimelineHint: "两只蛐蛐合并到同一条时间轴。这就是这场比赛的故事。",
  notVisible: "不可见",
  noDecisions: "还没有决策",


};

const DICTS: Record<Locale, Dict> = { en: EN, zh: ZH };

/**
 * Exposed for tooling. The tests use it to assert key parity and arity parity
 * between locales, which is what stops a locale from silently drifting.
 */
export { DICTS };

export function dict(locale: Locale): Dict {
  return DICTS[locale];
}

/* ------------------------------------------------------------------ */
/* Dynamic names                                                       */
/* ------------------------------------------------------------------ */

/**
 * Persona names.
 *
 * Chinese keeps an English gloss alongside the translation on purpose. A bare
 * translated name reads as a generic mob name; "收集者 THE COLLECTOR" reads as a
 * fighter's ring name, which is what the design docs are going for.
 */
const PERSONA_ZH: Record<string, string> = {
  "THE COLLECTOR": "收集者",
  "THE HUNTER": "猎手",
  "THE SCOUT": "侦察兵",
  "THE GAMBLER": "赌徒",
  "THE TURTLE": "龟缩流",
};

export function personaName(persona: string, locale: Locale): string {
  if (locale === "en") return persona;
  const zh = PERSONA_ZH[persona];
  return zh ? `${zh} ${persona}` : persona;
}

const ARCHETYPE_ZH: Record<string, { name: string; tagline: string }> = {
  "THE BERSERKER": { name: "狂战士", tagline: "先动手，从不思考。" },
  "THE DUELIST": { name: "决斗者", tagline: "只挑打得赢的架打。" },
  "THE GAMBLER": { name: "赌徒", tagline: "永远相信下一颗晶体值得赌。" },
  "THE SCOUT": { name: "侦察兵", tagline: "比对手更懂这张图。" },
  "THE TURTLE": { name: "龟缩流", tagline: "绝不输掉它从不发起的战斗。" },
  "THE COLLECTOR": { name: "收集者", tagline: "眼里只有晶体。" },
  "THE PHILOSOPHER": { name: "哲学家", tagline: "以极大的信念保存体力。" },
  "THE ALL-ROUNDER": { name: "全能选手", tagline: "没有明显弱点，也没有明显计划。" },
};

export function archetypeName(archetype: string, locale: Locale): string {
  if (locale === "en") return archetype;
  return ARCHETYPE_ZH[archetype]?.name ?? archetype;
}

export function archetypeTagline(
  archetype: string,
  tagline: string,
  locale: Locale,
): string {
  if (locale === "en") return tagline;
  return ARCHETYPE_ZH[archetype]?.tagline ?? tagline;
}

export function traitLabel(key: string, d: Dict): string {
  switch (key) {
    case "aggression":
      return d.traitAggression;
    case "exploration":
      return d.traitExploration;
    case "risk":
      return d.traitRisk;
    case "resourceFocus":
      return d.traitResourceFocus;
    case "intel":
      return d.traitIntel;
    case "patience":
      return d.traitPatience;
    case "survival":
      return d.traitSurvival;
    case "precision":
      return d.traitPrecision;
    default:
      return key;
  }
}

const RESOURCE_ZH: Record<ResourceKind, string> = {
  crystal: "晶体",
  energy: "能量",
  shield: "护盾",
  bomb: "爆炸物",
  blade: "旋转刀具",
  trap: "陷阱",
};

export function resourceKindName(kind: ResourceKind, locale: Locale): string {
  return locale === "zh" ? RESOURCE_ZH[kind] : kind;
}

/**
 * Action names for UI display.
 *
 * Protocol identifiers and model prompts stay English; this only translates
 * the labels the viewer sees (rules strip, rules page, timeline).
 */
const ACTION_ZH: Record<ActionType, string> = {
  MOVE: "移动",
  SCOUT: "侦察",
  COLLECT: "采集",
  ATTACK: "攻击",
  DEFEND: "防御",
  WAIT: "等待",
  FLEE: "逃离",
  EXCHANGE: "兑换",
  USE: "使用",
};

export function actionName(action: ActionType, locale: Locale = "en"): string {
  return locale === "zh" ? ACTION_ZH[action] : action;
}

/**
 * Agent reason strings.
 *
 * A closed set: the only agents that produce them are the heuristic bots and the
 * LLM wrapper's own fallbacks. A *real* model's reason is free-form and passes
 * through untranslated.
 */
const REASON_ZH: Record<string, string> = {




  "detonate on clustered enemy": "敌人扎堆，引爆",
  "spin up for close pressure": "近身压力，启动刀具",
  "buy blast for the duel": "兑换爆炸物准备对决",
  "hp critical, disengaging": "血量告急，脱离接触",
  "high-value contact, scanning before committing": "高价值信号，先侦察再决定",
  "in melee range": "已进入近战范围",
  "closing to engage": "拉近距离准备开打",
  "pressing a weakened enemy": "追击虚弱的对手",
  "channel keeps breaking, disengaging": "采集反复被打断，先撤",
  "resource in range": "资源已在交互范围",
  "moving to resource": "前往资源",
  "investigating last sighting": "去上一处目击点看看",
  "sweeping for contacts": "扫图寻找目标",
  "no intel, expanding search": "没有情报，扩大搜索",
  "[fallback] degraded: provider unavailable": "[降级] 模型不可用",
  "[fallback] skipped: previous decision still in flight": "[跳过] 上一次决策还没返回",
  "[fallback] provider error": "[兜底] 模型报错",
  "[fallback] unparseable response": "[兜底] 回复无法解析",
  "[fallback] decision wait timed out": "[超时] 决策等待超时",
};

export function reasonText(reason: string | undefined, locale: Locale): string | undefined {
  if (reason === undefined) return undefined;
  if (locale === "en") return reason;
  return REASON_ZH[reason] ?? reason;
}

/* ------------------------------------------------------------------ */
/* Rendering helpers                                                   */
/* ------------------------------------------------------------------ */

/** Renders a non-decision timeline entry as a sentence. */
export function describeEntry(
  entry: TimelineEntry,
  who: string,
  locale: Locale,
  d: Dict,
): string {
  switch (entry.kind) {
    case "collect":
      return d.collected(
        who,
        resourceKindName((entry.resourceKind ?? "crystal") as ResourceKind, locale),
        entry.value ?? 0,
      );
    case "trap":
      return d.steppedOnTrap(who);
    case "detect":
      return d.detectedTrap(who);
    case "interrupt":
      return d.interrupted(who);
    case "collectMiss":
      return d.collectMissed(who);
    case "kill":
      return entry.killedByTrap ? d.killedByTrap(who) : d.eliminated(who);
    case "flee":
      return d.fled(who);
    case "exchange":
      return d.exchanged(
        who,
        resourceKindName((entry.resourceKind ?? "bomb") as ResourceKind, locale),
        entry.value ?? 0,
      );
    case "use":
      return d.usedItem(
        who,
        resourceKindName((entry.resourceKind ?? "bomb") as ResourceKind, locale),
      );
    case "bomb":
      return d.detonatedBomb(who, entry.value ?? 0);
    case "blade":
      return d.spunBlade(who);
    default:
      return who;
  }
}

export function highlightText(h: MatchHighlight, who: string, d: Dict): string {
  switch (h.type) {
    case "energy":
      return d.momentEnergy(who);
    case "trap":
      return d.momentTrap(who);
    case "kill":
      return d.momentEliminated(who);
    case "interrupt":
      return d.momentInterrupt(who);
    default:
      return who;
  }
}
